import { useState } from 'react';

import { toAgendaPayload } from './agendaViewModel';
import { useAgendaDiscussionPoints } from './useAgendaDiscussionPoints';
import { useAgendaOpeningStatement } from './useAgendaOpeningStatement';
import { useAgendaTopics } from './useAgendaTopics';

import type { AgendaDraft, Editable } from './agendaDraft';
import type { AgendaContent, AgendaItemView } from './agendaViewModel';
import type { MeetingAgendaUpdatePayload } from '@/types/review/PostReviewMeeting';

interface UseAgendaEditorParams {
    /** The saved agenda, read once: only its language, opening statement and items are used, so the cycle
     * review's agenda and a thematic meeting's agenda (which has no `cycleInstanceId`) both fit. */
    agenda: AgendaContent;
    disabled: boolean;
    onSave: (payload: MeetingAgendaUpdatePayload) => void;
}

const NOOP = () => {};

/**
 * A point added in its row stays local until its title is confirmed, and the server refuses a point
 * without one: every save made meanwhile — a response left, a topic renamed — goes without it.
 */
const withNamedPointsOnly = (items: AgendaItemView[]): AgendaItemView[] =>
    items.map((item) => ({
        ...item,
        discussionPoints: item.discussionPoints.filter((point) => point.title.trim()),
    }));

/**
 * The editing state of a meeting agenda, each change saved at once (the agenda has no save button):
 * its opening statement, its topics and their discussion points. Shared by the two flows that edit an
 * agenda — the post-review meeting of a cycle and a thematic meeting — so the two never drift.
 *
 * The agenda is read once, on mount: from then on the editor owns its working copy. It is edited in its
 * own language, whatever the reader's, and saved in that language. A host whose agenda is replaced from
 * elsewhere (a generated agenda) mounts a fresh editor with a new `key`.
 */
export const useAgendaEditor = ({ agenda, disabled, onSave }: UseAgendaEditorParams) => {
    const [initialAgenda] = useState(agenda);
    const [items, setItems] = useState(initialAgenda.items);

    const saveAgenda = (nextItems: AgendaItemView[], openingStatement: string) =>
        onSave(
            toAgendaPayload(
                withNamedPointsOnly(nextItems),
                openingStatement,
                initialAgenda.language,
            ),
        );

    // The single read-only gate: on a completed meeting every change is a no-op, so no caller has to
    // check `disabled` again.
    const editable: Editable = (handler) => (disabled ? NOOP : handler);

    const openingStatement = useAgendaOpeningStatement({
        initialValue: initialAgenda.openingStatement,
        onSave: (value) => saveAgenda(items, value),
        editable,
    });

    const draft: AgendaDraft = {
        items,
        setItems,
        commit: (nextItems) => {
            setItems(nextItems);
            saveAgenda(nextItems, openingStatement.value);
        },
        save: () => saveAgenda(items, openingStatement.value),
        editable,
    };
    const topics = useAgendaTopics(draft);
    const points = useAgendaDiscussionPoints(draft);

    return { language: initialAgenda.language, items, openingStatement, topics, points };
};

/** Everything `AgendaEditor` and its hosts need from the hook above. */
export type AgendaEditorController = ReturnType<typeof useAgendaEditor>;
